import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../../redux/api";
import { usePermissions } from "../../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../../lib/permissions";
import {
  getAllCategorieslist,
  getAllSize,
} from "../../../../redux/size/action";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllBrands } from "../../../../redux/brand/action";
import { getAllOccassion } from "../../../../redux/occassion/action";
import { getAllColor } from "../../../../redux/color/action";
import { getAllTypes } from "../../../../redux/type/action";
import { getSubcategory } from "../../../../redux/subcategory/action";
import "../../BulkEdit/BulkEditStyle.css";

import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronRight,
  CloudUpload,
  FilterX,
  Inbox,
  Info,
  PackageCheck,
  SearchX,
  Sparkles,
  Upload,
  X,
  XCircle,
} from "lucide-react";

import { cn } from "lib/utils";
import PendingProductsFilter from "./PendingProductsFilter";
import "../../../Common/ListShell/ListShell.css";
import PendingProductsTable from "./PendingProductsTable";
import PendingProductsFooter from "./PendingProductsFooter";
import { uploadProductImage } from "../../../Common/ImageUpload/handleProductImageUpload";
import {
  TaxonomyErrorReport,
  normalizeFailedEntry,
  findRowForFailure,
} from "../../BulkEdit/TaxonomyErrors";
import { stripUnpermittedPriceFields } from "./form/payload";
import {
  ColumnVisibilityModal,
  BulkEditModal,
  CategorySubcategoryModal,
  ConfirmSubmitModal,
  DeleteConfirmModal,
} from "./PendingProductsModals";

// Columns shown in the Pending Products data-management table.
// Order here determines the order in the table; the first 18 entries are
// the ones visible by default (see DEFAULT_VISIBLE_COLUMN_KEYS below).
// Anything after that is opt-in via the Columns modal.
const DEFAULT_COLUMNS = [
  { key: "barcode", label: "Barcode" },
  { key: "name", label: "Name" },
  { key: "ar_name", label: "Arabic Name" },
  { key: "description", label: "Description" },
  { key: "ar_description", label: "Arabic Description" },
  { key: "brand", label: "Brands" },
  { key: "category", label: "Category" },
  { key: "ar_category", label: "Arabic Category" },
  { key: "sub_category", label: "Sub Category" },
  { key: "ar_sub_category", label: "Arabic Sub Category" },
  { key: "type", label: "Type" },
  { key: "ar_type", label: "Arabic Type" },
  { key: "is_b2b", label: "B2B" },
  { key: "cost", label: "Cost Price" },
  { key: "regular_price", label: "Regular Price" },
  { key: "sale_price", label: "Sale Price" },
  { key: "quantity", label: "Quantity" },
  { key: "image", label: "Image" },
  { key: "reference_link", label: "Image Link" },
  // --- Hidden by default; user can enable via Columns modal ---
  { key: "occasion", label: "Occasion" },
  { key: "product_option_type", label: "Product Option Type" },
  { key: "color", label: "Color" },
  { key: "size", label: "Size" },
  { key: "height", label: "Height" },
  { key: "width", label: "Width" },
  { key: "position", label: "Position" },
  { key: "new_from", label: "New From" },
  { key: "new_to", label: "New To" },
  { key: "status", label: "Status" },
];

// Columns visible by default — every Tier 1 required field from the dispatcher
// contract plus the pricing columns the manager needs to fill in before
// promoting. Everything else is still opt-in via the Columns modal.
// Order: identity → descriptions → image → taxonomy → flags → pricing → stock.
const DEFAULT_VISIBLE_COLUMN_KEYS = [
  "barcode",
  "name",
  "ar_name",
  "description",
  "ar_description",
  "image",
  "brand",
  "category",
  "sub_category",
  "type",
  "is_b2b",
  "cost",
  "regular_price",
  "sale_price",
  "quantity",
];

// Mirrors the dispatcher's Tier 1 required-field set. Promote reuses the same
// shared writer as the fast-path, so anything the dispatcher rejects on a
// fresh row would also be rejected here at promote time. Pricing fields stay
// in the list but the per-permission filter below drops them for managers
// who can't edit a given price scope.
// Exactly what the server refuses to PUBLISH — nothing else.
//
// Red must mean "this will stop you". Flagging fields the server happily
// accepts trains people to ignore red, and then a row that genuinely can't
// publish looks the same as 300 that can.
//
// Mirrors the backend's two promote gates:
//   PROMOTE_REQUIRED_IDENTITY_FIELDS = ("name",)      -> non-blank
//   PROMOTE_REQUIRED_FIELDS          = ("cost", "regular_price")  -> > 0
// plus `barcode`, which staging itself requires.
//
// Deliberately NOT here: ar_name, description, ar_description, brand,
// category, sub_category, type, is_b2b, quantity, sale_price. All optional —
// upload accepts them blank and publish does not check them.
// KEEP IN SYNC WITH THE BACKEND.
const REQUIRED_FIELDS = ["barcode", "name", "cost", "regular_price"];

// Fields the server requires to be a POSITIVE NUMBER, not merely present.
// Cleared numeric cells come back from staging as 0 / "0", which is non-blank
// and would otherwise read as filled — so the two fields that actually block
// publishing would render clean while harmless blanks lit up red.
const REQUIRED_POSITIVE_FIELDS = new Set(["cost", "regular_price"]);

// The client gate is advisory: the server owns the real rules, and blocking
// here only risks refusing something the server would accept. Cells still
// highlight so the admin can see what to fix before publishing.
const ENFORCE_REQUIRED_FIELDS = false;

const NUMERIC_FIELDS = ["cost", "regular_price", "sale_price", "quantity", "height", "width", "position"];

// Mirror of the same defaults used inside <PendingProductsFilter>. Kept here
// so this component can detect whether any filter is currently active without
// reaching into the child's state.
const DEFAULT_FILTERS = {
  title_filter_type: "1",
  title_filter_value: "",
  category: "",
  brand: "",
  occassion: "",
  sale_price_filter_type: ">",
  sale_price_value: "",
  barcode_filter_type: "1",
  barcode_filter_value: "",
};

const isFilterActive = (f) =>
  !!f.title_filter_value ||
  !!f.category ||
  !!f.brand ||
  !!f.occassion ||
  !!f.sale_price_value ||
  !!f.barcode_filter_value ||
  f.title_filter_type !== DEFAULT_FILTERS.title_filter_type ||
  f.barcode_filter_type !== DEFAULT_FILTERS.barcode_filter_type;

const PendingProducts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasMenuAccess, hasAction } = usePermissions();
  const canSeeCost = hasMenuAccess(MENU.PRODUCT_COSTS);
  const canEditCost = hasAction(MENU.PRODUCT_COSTS, PERMISSION.EDIT);
  const canSeeRegularPrice = hasMenuAccess(MENU.PRODUCT_REGULAR_PRICE);
  const canEditRegularPrice = hasAction(
    MENU.PRODUCT_REGULAR_PRICE,
    PERMISSION.EDIT,
  );
  const canSeeSalePrice = hasMenuAccess(MENU.PRODUCT_SALE_PRICE);
  const canEditSalePrice = hasAction(MENU.PRODUCT_SALE_PRICE, PERMISSION.EDIT);

  // VIEW-only sees a price column but the cell isn't editable. We still
  // require fields on the row only when EDIT is granted — VIEW-only managers
  // can't fix missing values anyway, so flagging them red would be noise.
  const requiredFields = useMemo(() => {
    const drop = new Set();
    if (!canEditCost) drop.add("cost");
    if (!canEditRegularPrice) drop.add("regular_price");
    if (!canEditSalePrice) drop.add("sale_price");
    return REQUIRED_FIELDS.filter((f) => !drop.has(f));
  }, [canEditCost, canEditRegularPrice, canEditSalePrice]);

  const visibleColumnDefs = useMemo(() => {
    const hide = new Set();
    if (!canSeeCost) hide.add("cost");
    if (!canSeeRegularPrice) hide.add("regular_price");
    if (!canSeeSalePrice) hide.add("sale_price");
    return DEFAULT_COLUMNS.filter((c) => !hide.has(c.key));
  }, [canSeeCost, canSeeRegularPrice, canSeeSalePrice]);

  // ========== CORE STATES ==========
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [validationErrors, setValidationErrors] = useState(new Map()); // Map<rowId, Set<fieldKey>>
  const [editedFields, setEditedFields] = useState({});

  // ========== PAGINATION ==========
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [paginationMeta, setPaginationMeta] = useState({});

  // ========== TOAST ==========
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [showToast, setShowToast] = useState(false);

  // ========== MODAL STATES ==========
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditValues, setBulkEditValues] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCategorySubcategoryModal, setShowCategorySubcategoryModal] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);
  const [modalValue, setModalValue] = useState({ category: [], sub_category: [] });
  const [subCategories, setSubCategories] = useState([]);
  // Per-row error entries shown in a structured modal. We use two separate
  // arrays because sync (PUT bulk-sync) and promote (POST bulk-insert) fail
  // for different reasons and the modal copy differs. Both populate `rowErrors`
  // (below) for the inline per-row badge.
  const [bulkInsertErrors, setBulkInsertErrors] = useState([]);
  const [bulkSyncErrors, setBulkSyncErrors] = useState([]);
  // Map<productId, errorMessage> — surfaces failing rows inline in the table
  // after the modal is dismissed. Cleared when the user edits the row or
  // resubmits successfully. Replaces the [object Object]-prone toast that
  // used to dump server errors into a 3-second banner.
  const [rowErrors, setRowErrors] = useState(new Map());

  // ========== COLUMNS & VISIBILITY ==========
  // Column order is fixed (no drag-to-reorder). The Columns modal still toggles
  // visibility via `visibleColumns` below.
  const columns = visibleColumnDefs;
  const [visibleColumns, setVisibleColumns] = useState(
    new Set(DEFAULT_VISIBLE_COLUMN_KEYS)
  );

  // ========== FILTERS ==========
  // Keep a single source of truth for the "off" state so the empty-result
  // branch can detect whether the user is filtering and offer a reset path
  // instead of dropping back to the upload-CTA empty state (which would
  // strand them — the filter bar disappears with `hasProducts=false`).
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // ========== REDUX ==========
  const [catparams] = useState({
    page: "",
    search_key: "",
    status: "",
  });

  useEffect(() => {
    dispatch(getAllCategorieslist(catparams));
    dispatch(getAllBrands(catparams));
    dispatch(getAllOccassion(catparams));
    dispatch(getAllColor(catparams));
    dispatch(getAllSize(catparams));
    dispatch(getAllTypes());
    dispatch(getSubcategory({ page: "", search_key: "", status: "" }));
    fetchProductData();
  }, [dispatch]);

  const categories = useSelector((state) => state.reusableSlice.catagoriesList);
  const brandList = useSelector((state) => state.reusableSlice.brandList);
  const occassionList = useSelector((state) => state.reusableSlice.occassionList);
  const colorList = useSelector((state) => state.reusableSlice.colorListData);
  const sizeList = useSelector((state) => state.reusableSlice.sizeListData);
  const typeList = useSelector((state) => state.reusableSlice.typeList) || [];
  const subcategoryList = useSelector((state) => state.reusableSlice.subcategoriesList) || [];

  // ========== DROPDOWN OPTIONS ==========
  // Memoized so the table + filter children get stable array refs across
  // unrelated parent re-renders (toast, modal toggles, edit-cell). Each list
  // only rebuilds when its source slice actually changes.
  const proOptionsCategory = useMemo(
    () =>
      Array.isArray(categories)
        ? categories.map((item) => ({ value: item?.title, label: item?.title, id: item?.id }))
        : [],
    [categories]
  );
  const proOptionsBrand = useMemo(
    () => (Array.isArray(brandList) ? brandList.map((item) => ({ value: item?.name, label: item?.name })) : []),
    [brandList]
  );
  const proOptionsOccassions = useMemo(
    () => (Array.isArray(occassionList) ? occassionList.map((item) => ({ value: item?.name, label: item?.name })) : []),
    [occassionList]
  );
  const proOptionsColor = useMemo(
    () => (Array.isArray(colorList) ? colorList.map((item) => ({ value: item.name, label: item.name })) : []),
    [colorList]
  );
  const proOptionsSize = useMemo(
    () => (Array.isArray(sizeList) ? sizeList.map((item) => ({ value: item.name, label: item.name })) : []),
    [sizeList]
  );
  const proOptionsType = useMemo(
    () => (Array.isArray(typeList) ? typeList.map((item) => ({ value: item.name, label: item.name })) : []),
    [typeList]
  );
  const proOptionsSubCategory = useMemo(
    () => (Array.isArray(subcategoryList) ? subcategoryList.map((item) => ({ value: item.title, label: item.title })) : []),
    [subcategoryList]
  );

  // ========== TOAST ==========
  // Stable across renders — only references setState setters, which React
  // guarantees are stable. Keeps downstream useCallbacks (fetch + handlers)
  // from regenerating on unrelated state changes.
  const showToastMessage = useCallback((message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    // Successes auto-dismiss quickly; warnings/errors linger so the user has
    // time to read them before they disappear.
    const dismissMs = type === "success" ? 3000 : 5000;
    setTimeout(() => setShowToast(false), dismissMs);
  }, []);

  // ========== HELPERS ==========
  const applyProductDefaults = (fetchedProducts) =>
    fetchedProducts.map((p) => ({ ...p }));

  // Build a case-insensitive lookup from the known categories list so we can
  // auto-correct category text that came in from CSV (e.g. "perfumes" → "perfume").
  const normalizeCategoryName = (rawName) => {
    if (!rawName || !categories || !Array.isArray(categories)) return rawName;
    const trimmed = rawName.trim();
    const match = categories.find(
      (cat) => cat.title && cat.title.toLowerCase() === trimmed.toLowerCase()
    );
    return match ? match.title : trimmed;
  };

  // Normalize a comma-separated category string (handles multi-category products).
  const normalizeCategoryField = (raw) => {
    if (!raw) return raw;
    return raw
      .split(",")
      .map((s) => normalizeCategoryName(s.trim()))
      .join(", ");
  };

  // Also resolve ar_category from the matched category so both stay in sync.
  const resolveArCategory = (rawName) => {
    if (!rawName || !categories || !Array.isArray(categories)) return "";
    return rawName
      .split(",")
      .map((s) => {
        const trimmed = s.trim();
        const match = categories.find(
          (cat) => cat.title && cat.title.toLowerCase() === trimmed.toLowerCase()
        );
        return match?.ar_title || "";
      })
      .join(", ");
  };

  // Resolve category name(s) to comma-separated numeric IDs so the backend
  // can use the ID directly instead of doing name-based lookup.
  const resolveCategoryIds = (raw) => {
    if (!raw || !categories || !Array.isArray(categories)) return null;
    const ids = raw
      .split(",")
      .map((s) => {
        const trimmed = s.trim();
        const match = categories.find(
          (cat) => cat.title && cat.title.toLowerCase() === trimmed.toLowerCase()
        );
        return match?.id;
      })
      .filter(Boolean);
    return ids.length > 0 ? ids.join(",") : null;
  };

  // Pre-flight UX validation only: flag empty required cells so the user
  // gets instant red highlights and a disabled Submit button. Anything
  // beyond presence (category-name lookup, FK existence, business rules)
  // is the server's job — it returns row-level errors via bulkSyncErrors /
  // bulkInsertErrors / rowErrors and we render those directly. Trying to
  // mirror server rules here just creates drift when the server changes.
  const validateProducts = useCallback((productList = null) => {
    const errors = new Map();
    const list = productList || products.filter((p) => selectedRows.has(p.id));
    list.forEach((p) => {
      const missingFields = new Set();
      for (const field of requiredFields) {
        const val = p[field];
        const blank =
          val === null || val === undefined || String(val).trim() === "";

        if (REQUIRED_POSITIVE_FIELDS.has(field)) {
          // Publish rejects these unless they are greater than zero, and a
          // cleared cell comes back from staging as 0 — non-blank, so a
          // presence test alone would call it filled and leave the row
          // looking clean right up until Publish refuses it.
          const numeric = Number(val);
          if (blank || !Number.isFinite(numeric) || numeric <= 0) {
            missingFields.add(field);
          }
          continue;
        }

        if (blank) {
          missingFields.add(field);
        }
      }
      if (missingFields.size > 0) {
        errors.set(p.id, missingFields);
      }
    });
    setValidationErrors(errors);
    // Highlights still render from `errors`; only the pass/fail gate is relaxed.
    return ENFORCE_REQUIRED_FIELDS ? errors.size === 0 : true;
  }, [products, selectedRows, requiredFields]);

  // ========== FETCH ==========
  const fetchProductData = useCallback(async (pageNum = page, pageSize = perPage) => {
    setIsLoadingData(true);
    try {
      const response = await axiosInstance.get("admin/temp-products/bulk-uploads", {
        params: { page: pageNum, per_page: pageSize },
      });
      const fetched = response.data.response.products || [];
      const meta = response.data.response.pagination_meta || {};
      const withDefaults = applyProductDefaults(fetched);
      setProducts(withDefaults);
      setPaginationMeta(meta);
      validateProducts(withDefaults);
      setSelectedRows(new Set());
    } catch (error) {
      console.error("Failed to fetch data:", error);
      showToastMessage("Failed to load product data", "danger");
    } finally {
      setIsLoadingData(false);
    }
  }, [page, perPage, validateProducts, showToastMessage]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchProductData(newPage, perPage);
  }, [fetchProductData, perPage]);

  const handlePerPageChange = useCallback((newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
    fetchProductData(1, newPerPage);
  }, [fetchProductData]);

  // Image upload now goes through POST /uploads/images with config_type: "product"
  // — the backend handles WebP conversion and multi-size resizing. See uploadProductImage.

  // ========== ROW SELECTION ==========
  const handleSelectRow = useCallback((id) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedRows((prev) =>
      prev.size === products.length ? new Set() : new Set(products.map((p) => p.id))
    );
  }, [products]);

  // ========== ROW OPERATIONS ==========
  const handleAddRow = useCallback(async (count) => {
    try {
      const template = {
        name: "New Product", ar_name: "", occasion: "", brand: "", category: "",
        sub_category: "", description: "", ar_description: "", image_urls: null,
        type: "",
        cost: 0, regular_price: 0, sale_price: 0,
        product_option_type: "", color: "", size: "",
        // `position` seeds blank, never 0. It is a merchandising pin, and 0
        // sorts *above* 1 — seeding it meant every manually added row went
        // live pinned ahead of the whole catalogue. Blank = not featured.
        height: "", width: "", position: "",
        new_from: "", new_to: "",
        barcode: "", quantity: 0, status: "Active",
      };
      const rows = Array.from({ length: count }, () => ({ ...template }));
      await axiosInstance.post("admin/temp-products/bulk-sync", { products: rows });
      await fetchProductData();
      showToastMessage(`${count} row(s) added`);
    } catch (error) {
      console.error("Failed to add rows:", error);
      showToastMessage("Failed to add rows", "danger");
    }
  }, [fetchProductData, showToastMessage]);

  const handleDuplicateRows = useCallback(async (count) => {
    if (selectedRows.size === 0) {
      showToastMessage("Please select rows to duplicate", "warning");
      return;
    }
    try {
      const selected = products.filter((p) => selectedRows.has(p.id));
      const duplicates = [];
      selected.forEach((product) => {
        for (let i = 0; i < count; i++) {
          const { id, ...rest } = product;
          duplicates.push({
            ...rest,
            name: `${product.name} (Copy ${i + 1})`,
            // Barcode is unique per product; user must enter a new one.
            barcode: "",
          });
        }
      });
      await axiosInstance.post("admin/temp-products/bulk-sync", { products: duplicates });
      await fetchProductData();
      setSelectedRows(new Set());
      showToastMessage(`${duplicates.length} row(s) duplicated`);
    } catch (error) {
      console.error("Duplicate failed:", error);
      showToastMessage("Failed to duplicate rows", "danger");
    }
  }, [selectedRows, products, fetchProductData, showToastMessage]);

  const handleDeleteRows = useCallback(async () => {
    if (selectedRows.size === 0) {
      showToastMessage("Please select rows to delete", "warning");
      return;
    }
    setShowDeleteConfirm(true);
  }, [selectedRows, showToastMessage]);

  // Stable modal-open callbacks. Inline arrows on the table props would
  // create new refs every render and defeat the React.memo on the table.
  const handleShowColumnModal = useCallback(() => setShowColumnModal(true), []);
  const handleShowBulkEditModal = useCallback(() => setShowBulkEditModal(true), []);

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      const ids = Array.from(selectedRows);
      const res = await axiosInstance.post("admin/temp-products/bulk-delete", { ids });
      const deleted = res?.data?.deleted ?? ids.length;

      // Drop edits for the deleted rows so they don't get resurrected on the
      // next save. We always do this regardless of how the refetch resolves.
      const newEdited = { ...editedFields };
      ids.forEach((id) => delete newEdited[id]);
      setEditedFields(newEdited);
      setSelectedRows(new Set());

      // Refetch instead of filtering in-memory so later pages backfill into
      // the current view (deleting 4 of 50 on page 1 should leave 50 again,
      // pulled from page 2 — not 46 with a sparse footer). If the deletion
      // emptied the current page entirely AND we're past page 1, step back
      // one page so the user isn't stranded on an empty page > 1.
      const remainingOnPage = products.length - deleted;
      const targetPage = remainingOnPage <= 0 && page > 1 ? page - 1 : page;
      if (targetPage !== page) setPage(targetPage);
      await fetchProductData(targetPage, perPage);

      if (deleted < ids.length) {
        showToastMessage(
          `${deleted} of ${ids.length} row(s) deleted — the rest were missing or belong to another session`,
          "warning"
        );
      } else {
        showToastMessage(`${deleted} row(s) deleted`);
      }
    } catch (error) {
      console.error("Delete failed:", error);
      showToastMessage("Failed to delete rows", "danger");
    }
  };

  // "Discard edits" must ONLY revert unsaved cell edits — it must not call
  // bulk-delete. The previous implementation deleted every product on the
  // current page (97 → 45 row loss bug) because it was bound to the same
  // delete endpoint as the destructive "Delete" action with no separation.
  // The correct behaviour: throw away the local diff, then re-fetch so the
  // table re-reads pristine values from the DB.
  const handleDiscard = async () => {
    if (Object.keys(editedFields).length === 0) {
      showToastMessage("No edits to discard", "info");
      return;
    }
    setEditedFields({});
    setRowErrors(new Map());
    setSelectedRows(new Set());
    await fetchProductData(page, perPage);
    showToastMessage("Unsaved edits discarded", "info");
  };

  // ========== COLUMN VISIBILITY ==========
  const handleColumnToggle = (key) => {
    const next = new Set(visibleColumns);
    next.has(key) ? next.delete(key) : next.add(key);
    setVisibleColumns(next);
  };

  // ========== CELL EDITING ==========
  const handleCellEdit = useCallback(async (rowId, columnKey, currentValue) => {
    // VIEW-only price scopes show a value but the cell isn't editable. The
    // backend would 422 on a write either way; short-circuit here so the cell
    // never enters edit mode and the user gets no edit affordance.
    if (columnKey === "cost" && !canEditCost) return;
    if (columnKey === "regular_price" && !canEditRegularPrice) return;
    if (columnKey === "sale_price" && !canEditSalePrice) return;
    if (columnKey === "image") {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
          const { urls } = await uploadProductImage(file);
          const imagesPayload = [{ image_urls: urls, sort_order: 0 }];
          setProducts((prev) =>
            prev.map((p) =>
              p.id === rowId ? { ...p, image_urls: urls, images: imagesPayload } : p
            )
          );
          setEditedFields((prev) => ({
            ...prev,
            [rowId]: {
              ...(prev[rowId] || {}),
              image_urls: urls,
              images: imagesPayload,
            },
          }));
          showToastMessage("Image uploaded successfully");
        } catch {
          showToastMessage("Image upload failed", "danger");
        }
      };
      input.click();
    } else {
      setEditingCell({ row: rowId, column: columnKey });
      setEditValue(String(currentValue ?? ""));
    }
  }, [canEditCost, canEditRegularPrice, canEditSalePrice, showToastMessage]);

  const handleCellSave = useCallback((newValue) => {
    if (!editingCell) return;
    const { row, column } = editingCell;
    let finalValue = newValue ?? editValue;

    // For numeric fields: parse to a real number. If invalid/empty, fall back to
    // the original row value — never silently default to 0.
    if (NUMERIC_FIELDS.includes(column)) {
      const parsed = parseFloat(finalValue);
      if (isNaN(parsed) || String(finalValue).trim() === "") {
        const original = products.find((p) => p.id === row);
        finalValue = original != null ? original[column] : finalValue;
      } else {
        finalValue = parsed;
      }
    }

    // Auto-sync ar_type when type changes
    const extraFields = {};
    if (column === "type") {
      const matchedType = typeList.find((t) => t.name === finalValue);
      extraFields.ar_type = matchedType?.ar_name || "";
    }

    const updatedProducts = products.map((product) => {
      if (product.id !== row) return product;
      return { ...product, [column]: finalValue, ...extraFields };
    });
    setProducts(updatedProducts);

    const updatedProduct = updatedProducts.find((p) => p.id === row);
    const fieldsToSync = { [column]: finalValue, ...extraFields };
    setEditedFields((prev) => ({
      ...prev,
      [row]: { ...(prev[row] || {}), ...fieldsToSync },
    }));

    // Clear the row's error badge once the user starts fixing it.
    setRowErrors((prev) => {
      if (!prev.has(row)) return prev;
      const next = new Map(prev);
      next.delete(row);
      return next;
    });

    if (requiredFields.includes(column) && updatedProduct) {
      setValidationErrors((prev) => {
        const next = new Map(prev);
        const missingFields = new Set();
        for (const f of requiredFields) {
          const val = updatedProduct[f];
          if (val === null || val === undefined || String(val).trim() === "") {
            missingFields.add(f);
          }
        }
        if (missingFields.size > 0) {
          next.set(row, missingFields);
        } else {
          next.delete(row);
        }
        return next;
      });
    }

    setEditingCell(null);
    setEditValue("");
  }, [editingCell, editValue, products, typeList, requiredFields]);

  // ========== CATEGORY MODAL ==========
  const handleOpenCategoryModal = useCallback((product) => {
    setModalProduct(product);
    const currentCategories = product.category?.split(",").map((s) => s.trim()) || [];
    const currentSubCategories = product.sub_category?.split(",").map((s) => s.trim()) || [];
    const selectedCats = proOptionsCategory.filter((opt) => currentCategories.includes(opt.value));

    setModalValue({ category: selectedCats, sub_category: [] });
    setShowCategorySubcategoryModal(true);

    const ids = selectedCats.map((opt) => opt.id).join(",");
    if (ids) {
      axiosInstance
        .get("admin/sub-categories", { params: { category_ids: ids, page: 1, search_key: "", status: "" } })
        .then((res) => {
          const options = (res.data.response || []).map((item) => ({
            value: item.title,
            label: item.title,
          }));
          setSubCategories(options);
          setModalValue((prev) => ({
            ...prev,
            sub_category: options.filter((opt) => currentSubCategories.includes(opt.value)),
          }));
        })
        .catch((err) => console.error("Failed to fetch subcategories:", err));
    }
  }, [proOptionsCategory]);

  const handleCategoryModalDone = () => {
    const newCategory = modalValue.category.map((v) => v.value).join(", ");
    const newCategoryId = modalValue.category.map((v) => v.id).filter(Boolean).join(",");
    const newSubCategory =
      modalValue.category.length === 0
        ? ""
        : modalValue.sub_category.map((v) => v.value).join(", ");
    const updatedProducts = products.map((p) =>
      p.id === modalProduct.id
        ? { ...p, category: newCategory, category_id: newCategoryId, sub_category: newSubCategory }
        : p
    );
    setProducts(updatedProducts);
    setEditedFields((prev) => ({
      ...prev,
      [modalProduct.id]: {
        ...(prev[modalProduct.id] || {}),
        category: newCategory,
        category_id: newCategoryId,
        sub_category: newSubCategory,
      },
    }));
    // Re-validate so the red highlight clears immediately after picking a valid category.
    validateProducts(updatedProducts);
    setShowCategorySubcategoryModal(false);
  };

  // ========== FILTERS ==========
  // Single submit path. The filter component owns a local draft and passes
  // the resolved object here on Apply / chip-clear / Reset, so we don't have
  // to wait for setFilters() to commit before fetching with new values.
  const handleFilterSubmit = async (next) => {
    const effective = next ?? filters;
    try {
      setPage(1);
      setFilters(effective);
      const response = await axiosInstance.get("admin/temp-products/bulk-uploads", {
        params: { ...effective, page: 1, per_page: perPage },
      });
      const fetched = response.data.response.products || [];
      const meta = response.data.response.pagination_meta || {};
      const withDefaults = applyProductDefaults(fetched);
      setProducts(withDefaults);
      setPaginationMeta(meta);
      validateProducts(withDefaults);
      showToastMessage("Filters applied successfully");
    } catch (error) {
      console.error("API Error:", error);
      showToastMessage("Failed to apply filters", "danger");
    }
  };

  // ========== BULK EDIT ==========
  const handleApplyBulkEdit = () => {
    // Filter out unchecked (empty-string) entries; parse numeric fields to numbers.
    const changed = Object.fromEntries(
      Object.entries(bulkEditValues)
        .filter(([_, v]) => v !== "")
        .map(([k, v]) => {
          if (NUMERIC_FIELDS.includes(k)) {
            const parsed = parseFloat(v);
            return [k, isNaN(parsed) ? v : parsed];
          }
          return [k, v];
        })
    );
    if (Object.keys(changed).length === 0) {
      showToastMessage("No values to apply", "warning");
      return;
    }
    // Auto-sync ar_type when type is bulk-edited
    if ("type" in changed) {
      const matchedType = typeList.find((t) => t.name === changed.type);
      changed.ar_type = matchedType?.ar_name || "";
    }
    const updatedProducts = products.map((p) =>
      selectedRows.has(p.id) ? { ...p, ...changed } : p
    );
    setProducts(updatedProducts);
    const newEdited = { ...editedFields };
    selectedRows.forEach((id) => {
      newEdited[id] = { ...(newEdited[id] || {}), ...changed };
    });
    setEditedFields(newEdited);
    // Re-run validation so fixed required fields immediately lose their red highlight.
    validateProducts(updatedProducts);
    setShowBulkEditModal(false);
    setBulkEditValues({});
    setSelectedRows(new Set());
    showToastMessage("Bulk edit applied successfully");
  };

  // ========== SUBMIT ==========
  const handleSubmitClick = () => {
    if (selectedRows.size === 0) {
      showToastMessage("Please select rows to submit", "warning");
      return;
    }
    if (!validateProducts()) {
      // Check if any errors are specifically about unrecognized categories
      const selected = products.filter((p) => selectedRows.has(p.id));
      const knownNames = new Set((categories || []).map((c) => c.title?.toLowerCase()));
      const badCats = selected.filter((p) => {
        if (!p.category) return false;
        return p.category.split(",").map((s) => s.trim()).filter(Boolean)
          .some((n) => !knownNames.has(n.toLowerCase()));
      });
      if (badCats.length > 0) {
        const names = [...new Set(badCats.flatMap((p) =>
          p.category.split(",").map((s) => s.trim()).filter((n) => !knownNames.has(n.toLowerCase()))
        ))];
        showToastMessage(
          `Unrecognized category: "${names.join('", "')}" — use the category picker to select the correct one. Also check for missing required fields.`,
          "danger"
        );
      } else {
        showToastMessage(
          "Some rows have missing required fields. Fix red-highlighted cells before saving.",
          "danger"
        );
      }
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);
    try {
      const selectedIds = Array.from(selectedRows);
      // Merge the full original row with only the changed fields so bulk-sync
      // always receives a complete product object — prevents the backend from
      // zeroing out fields that weren't explicitly edited.
      const changedProducts = Object.entries(editedFields)
        .filter(([id]) => selectedRows.has(Number(id)))
        .map(([id, fields]) => {
          const original = products.find((p) => p.id === Number(id)) || {};
          const merged = { ...original, ...fields };
          // Backend rejects price fields on PUT /bulk-sync without EDIT —
          // strip even though VIEW-only users couldn't have edited them
          // client-side.
          return stripUnpermittedPriceFields(merged, {
            canEditCost,
            canEditRegularPrice,
            canEditSalePrice,
          });
        });

      // Ensure every selected product has the correct category name, category_id,
      // and ar_category before submission. The backend's bulk-insert resolves
      // categories by ID — if the temp row has a stale/missing category_id the
      // type validation fails even when the name looks right.
      const selectedProducts = products.filter((p) => selectedRows.has(p.id));
      const changedById = new Map(changedProducts.map((p) => [p.id, p]));
      selectedProducts.forEach((p) => {
        const normalized = normalizeCategoryField(p.category);
        const categoryId = resolveCategoryIds(normalized);
        const arCategory = resolveArCategory(normalized);
        const needsFix =
          normalized !== p.category ||
          categoryId !== p.category_id ||
          !p.category_id;

        if (needsFix || categoryId) {
          const existing = changedById.get(p.id);
          const patch = {
            category: normalized,
            category_id: categoryId,
            ar_category: arCategory,
          };
          if (existing) {
            Object.assign(existing, patch);
          } else {
            const full = { ...p, ...patch };
            changedProducts.push(full);
            changedById.set(p.id, full);
          }
        }
      });

      // Normalize fields from display format to backend's expected format.
      changedProducts.forEach((p) => {
        // is_b2b: "yes"/"no" → 1/0
        if (p.is_b2b === "yes" || p.is_b2b === true) p.is_b2b = 1;
        else if (p.is_b2b === "no" || p.is_b2b === false) p.is_b2b = 0;

        // type: name string → numeric ID (backend expects integer)
        if (p.type && typeof p.type === "string") {
          const matched = typeList.find((t) => t.name === p.type);
          if (matched) p.type = matched.id;
        }

        // The backend's temporary_product table has NOT NULL columns for
        // string fields like occasion, tags, sub_category, etc. Convert
        // null/undefined to empty string so the UPDATE doesn't fail.
        const notNullStringFields = [
          "occasion", "tags", "sub_category", "ar_sub_category",
          "color", "size", "barcode", "brand",
          "ar_category", "ar_name", "ar_description",
          "description", "name", "reference_link",
          "product_option_type", "ar_type",
        ];
        notNullStringFields.forEach((f) => {
          if (p[f] === null || p[f] === undefined) p[f] = "";
        });
      });

      // Diagnostic: log ALL selected products' type values + the full type ID map
      console.log("[PendingProducts] typeList (id→name):", typeList.map((t) => ({ id: t.id, name: t.name })));
      console.log("[PendingProducts] selected products type info:", selectedProducts.map((p) => ({
        id: p.id, name: p.name, type: p.type, typeIsString: typeof p.type === "string",
        category: p.category, category_id: p.category_id,
      })));

      if (changedProducts.length > 0) {
        // Debug: log what we're sending so we can verify type/category_id reach the backend
        console.log("[PendingProducts] bulk-sync payload:", changedProducts.map((p) => ({
          id: p.id, name: p.name, type: p.type, ar_type: p.ar_type,
          category: p.category, category_id: p.category_id, is_b2b: p.is_b2b,
        })));
        const syncRes = await axiosInstance.put("admin/temp-products/bulk-sync", { products: changedProducts });
        console.log("[PendingProducts] bulk-sync response:", syncRes.data);

        // Check if bulk-sync had failures — if it did, the temp table still has
        // old data and bulk-insert would create products with stale values.
        const syncData = syncRes.data?.response || {};
        const syncFailed = syncData.failed || [];
        if (syncFailed.length > 0) {
          console.error("[PendingProducts] bulk-sync failures:", syncFailed);
          // Map failed entries back to rows by id, then barcode — never by
          // name. Names are optional in staging, so a name match resolves to
          // "" for most failures and lands on the first blank-name row.
          const syncRowErrors = new Map();
          syncFailed.forEach((entry) => {
            const normalized = normalizeFailedEntry(entry);
            const match = findRowForFailure(changedProducts, normalized);
            if (match) syncRowErrors.set(match.id, normalized.message);
          });
          setRowErrors((prev) => {
            const next = new Map(prev);
            changedProducts.forEach((p) => next.delete(p.id));
            syncRowErrors.forEach((msg, id) => next.set(id, msg));
            return next;
          });
          setBulkSyncErrors(syncFailed);
          showToastMessage(
            `Couldn't save ${syncFailed.length} row${syncFailed.length > 1 ? "s" : ""} — see details.`,
            "warning"
          );
          setIsSubmitting(false);
          return; // Abort — don't insert with stale data
        }
      }
      const insertRes = await axiosInstance.post("admin/temp-products/bulk-insert", { ids: selectedIds });
      console.log("[PendingProducts] bulk-insert response:", insertRes.data);

      const responseData = insertRes.data?.response;
      const resultObj = Array.isArray(responseData) ? responseData[0] : responseData;
      const failed = resultObj?.failed || [];
      // Rows that DID publish but lost something on the way — currently an
      // orphan sub-category (named with no parent category) dropped so the
      // row could go live. The pending row is deleted once a product
      // publishes, so this response is the only surviving record of what was
      // dropped; if we don't show it, the value is gone with no trace.
      const publishWarnings = resultObj?.warnings || [];
      let successMsg = resultObj?.message || "";

      // Low-margin rows the server held back: admin promote → margin below
      // the minimum → "are you sure you want to continue?". Yes → re-submit
      // exactly those ids with confirmed_margin_ids and they publish with
      // prices as entered. No → they simply stay in the pending list —
      // not an error, a decision.
      const needsConfirmation = resultObj?.needs_confirmation || [];
      if (needsConfirmation.length > 0) {
        const MAX_LINES = 12;
        const lines = needsConfirmation.slice(0, MAX_LINES).map((w) => w.message);
        if (needsConfirmation.length > MAX_LINES) {
          lines.push(`…and ${needsConfirmation.length - MAX_LINES} more`);
        }
        const proceed = window.confirm(
          `${lines.join("\n")}\n\nAre you sure you want to continue?`
        );
        if (proceed) {
          const confirmIds = needsConfirmation.map((w) => w.id);
          const retryRes = await axiosInstance.post(
            "admin/temp-products/bulk-insert",
            { ids: confirmIds, confirmed_margin_ids: confirmIds }
          );
          const retryData = retryRes.data?.response;
          const retryObj = Array.isArray(retryData) ? retryData[0] : retryData;
          // Fold the retry's outcome into the one report the code below
          // already renders: failures get badges, warnings get the panel,
          // and the toast shows one combined created-count.
          failed.push(...(retryObj?.failed || []));
          publishWarnings.push(...(retryObj?.warnings || []));
          const count = (msg) => parseInt(msg, 10) || 0;
          successMsg = `${count(successMsg) + count(retryObj?.message)} products created successfully.`;
        } else {
          showToastMessage(
            `${needsConfirmation.length} product(s) kept in pending — margin below the minimum.`,
            "warning"
          );
        }
      }

      // Map failed entries back to product ids by name. The backend's failed[]
      // contains "<name> – <error>" strings; we look up against rows we just
      // submitted so the badge can be shown inline. Always run — when failed
      // is empty, this clears any prior annotation on the resubmitted ids.
      const submittedRows = products.filter((p) => selectedIds.includes(p.id));
      const newFailuresById = new Map();
      failed.forEach((entry) => {
        const normalized = normalizeFailedEntry(entry);
        const match = findRowForFailure(submittedRows, normalized);
        if (match) newFailuresById.set(match.id, normalized.message);
      });
      setRowErrors((prev) => {
        const next = new Map(prev);
        // Clear prior annotations on rows we just resubmitted; re-add only
        // the ones that failed this time.
        selectedIds.forEach((id) => next.delete(id));
        newFailuresById.forEach((msg, id) => next.set(id, msg));
        return next;
      });

      if (failed.length > 0) {
        // Strict-taxonomy errors can list several missing items across multiple
        // groups; hand the raw strings to TaxonomyErrorReport for untruncated
        // rendering with deep-links to the relevant admin pages.
        setBulkInsertErrors(failed);
        if (successMsg) {
          showToastMessage(
            `${successMsg} ${failed.length} row(s) failed — see details.`,
            "warning"
          );
        }
      } else if (publishWarnings.length > 0) {
        // Published, but with something dropped. Surfaced through the same
        // report component as failures so the detail isn't truncated — the
        // admin needs the barcode and the dropped value to repair it on the
        // live product.
        setBulkInsertErrors(
          publishWarnings.map((w) => ({
            identifier: w.identifier || w.barcode || "",
            error: w.warning || w.error || "",
          }))
        );
        showToastMessage(
          `${successMsg} ${publishWarnings.length} published with missing ` +
            `taxonomy — see details.`,
          "warning"
        );
      } else {
        showToastMessage(`${successMsg || `${selectedIds.length} products saved to Product List!`}`, "success");
      }

      // Re-fetch so failed products (still in temp table) remain visible
      await fetchProductData();
      setSelectedRows(new Set());
      setEditedFields({});
    } catch (error) {
      console.error("Submit failed:", error);
      showToastMessage(error?.response?.data?.message || "Failed to submit products", "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    setIsSubmitting(true);
    try {
      // Same as handleConfirmSubmit: always send the full row so bulk-sync
      // cannot zero out fields that weren't part of this edit session.
      const changed = Object.entries(editedFields).map(([id, fields]) => {
        const original = products.find((p) => p.id === Number(id)) || {};
        const merged = { ...original, ...fields };
        return stripUnpermittedPriceFields(merged, {
          canEditCost,
          canEditRegularPrice,
          canEditSalePrice,
        });
      });
      if (changed.length === 0) {
        showToastMessage("No edits to save", "warning");
        setIsSubmitting(false);
        return;
      }
      // Normalize fields before sending (backend expects integers)
      changed.forEach((p) => {
        if (p.is_b2b === "yes" || p.is_b2b === true) p.is_b2b = 1;
        else if (p.is_b2b === "no" || p.is_b2b === false) p.is_b2b = 0;
        if (p.type && typeof p.type === "string") {
          const matched = typeList.find((t) => t.name === p.type);
          if (matched) p.type = matched.id;
        }
      });
      const syncRes = await axiosInstance.put("admin/temp-products/bulk-sync", { products: changed });
      const syncFailed = syncRes.data?.response?.failed || [];

      // Map failures back to row ids and surface them via the modal + inline
      // badges. Always run so a successful retry clears prior annotations.
      const draftRowErrors = new Map();
      syncFailed.forEach((entry) => {
        const normalized = normalizeFailedEntry(entry);
        const match = findRowForFailure(changed, normalized);
        if (match) draftRowErrors.set(match.id, normalized.message);
      });
      setRowErrors((prev) => {
        const next = new Map(prev);
        changed.forEach((p) => next.delete(p.id));
        draftRowErrors.forEach((msg, id) => next.set(id, msg));
        return next;
      });

      if (syncFailed.length > 0) {
        console.error("[PendingProducts] save-draft failures:", syncFailed);
        setBulkSyncErrors(syncFailed);
        showToastMessage(
          `Couldn't save ${syncFailed.length} row${syncFailed.length > 1 ? "s" : ""} — see details.`,
          "warning"
        );
      } else {
        showToastMessage("Draft saved successfully!", "success");
        setEditedFields({});
      }
    } catch (error) {
      console.error("Save Draft failed:", error);
      showToastMessage(error?.response?.data?.message || "Failed to save draft", "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========== RENDER ==========
  // The bulk-uploads endpoint returns `pagination_meta` like:
  //   { current_page, has_more, limit, page, per_page, total: null, total_pages: null }
  // i.e. `total`/`total_pages` are unset — the backend only signals "is there
  // another page?" via `has_more`. We support both modes:
  //  - Known total: full numbered pagination + "X of Y rows" status line.
  //  - has_more only: simple Prev/Next strip + "showing N rows" status line.
  // (The known-total branch also covers other endpoints that DO populate it.)
  const knownTotal =
    paginationMeta.total ??
    paginationMeta.total_count ??
    paginationMeta.count ??
    null;
  const knownTotalPages =
    paginationMeta.pages ??
    paginationMeta.total_pages ??
    paginationMeta.last_page ??
    (knownTotal != null ? Math.max(1, Math.ceil(knownTotal / perPage)) : null);
  const hasMore = paginationMeta.has_more === true;

  // Aggregates surfaced both in the page header (global actions) and in the
  // table's status line (meta). Computed here so the same numbers drive both.
  const editedCount = Object.keys(editedFields).length;
  const selectedCount = selectedRows.size;
  const hasProducts = products.length > 0;
  const hasActiveFilters = isFilterActive(filters);
  // The filtered-empty state is "user has applied filters but the server
  // returned zero rows". We must keep the filter UI mounted in this case so
  // the user can clear or change the filter without leaving the page.
  const showFilteredEmpty = !isLoadingData && !hasProducts && hasActiveFilters;
  // Mount the workspace whenever there are rows to show, an active filter,
  // or the user has navigated past page 1. The page>1 case matters because
  // the post-delete step-back logic is bounded — if the backend race-loses
  // the last row on a deep page, the user must still see the footer to step
  // back manually instead of being kicked into the upload-CTA empty state.
  const showWorkspace = hasProducts || hasActiveFilters || page > 1;

  const toastAccent = {
    success: { stripe: "before:bg-nysmah-success", iconBg: "bg-nysmah-success", Icon: Check },
    warning: { stripe: "before:bg-nysmah-warning", iconBg: "bg-nysmah-warning", Icon: AlertTriangle },
    danger:  { stripe: "before:bg-nysmah-danger",  iconBg: "bg-nysmah-danger",  Icon: XCircle },
    info:    { stripe: "before:bg-nysmah-info",    iconBg: "bg-nysmah-info",    Icon: Info },
  }[toastType] || {
    stripe: "before:bg-nysmah-muted",
    iconBg: "bg-nysmah-muted",
    Icon: Info,
  };
  const ToastIcon = toastAccent.Icon;

  // Count ROWS with issues, not issues. A row can be in both maps (a missing
  // required cell AND a server rejection); summing the sizes counted it twice
  // and could report more problem rows than exist on the page.
  const errorCount = useMemo(
    () => new Set([...rowErrors.keys(), ...validationErrors.keys()]).size,
    [rowErrors, validationErrors]
  );

  // The bulk-uploads endpoint doesn't return a true total (`total: null`),
  // only a `has_more` flag. Reconstruct the best estimate the UI can show:
  //   - Sum of fully-paged rows so far + the partial current page.
  //   - Add a "+" suffix while there is more after the current page so the
  //     user knows it's a lower bound, not the final dataset size.
  // When `knownTotal` IS provided (other endpoints), prefer it directly.
  const seenSoFar = (page - 1) * perPage + products.length;
  const totalRowsLabel =
    knownTotal != null
      ? String(knownTotal)
      : `${seenSoFar}${hasMore ? "+" : ""}`;
  const totalRowsHint =
    knownTotal != null
      ? null
      : hasMore
        ? "More on next page"
        : page > 1
          ? "Across all pages"
          : null;

  return (
    <div className="nl-page">
      {/* Crumb */}
      <div className="nl-topbar">
        <span className="nl-topbar__crumb">Catalog</span>
        <ChevronRight size={12} className="nl-topbar__sep" aria-hidden="true" />
        <span className="nl-topbar__current">Pending products</span>
        {hasProducts && (
          <span className="nl-topbar__count">{totalRowsLabel}</span>
        )}
        {hasProducts && editedCount > 0 ? (
          <span
            className="nb-topbar__draft"
            style={{ marginLeft: "auto" }}
            title={`${editedCount} unsaved edit${editedCount === 1 ? "" : "s"}`}
          >
            <span className="nb-topbar__dot" aria-hidden="true" />
            {editedCount} unsaved
          </span>
        ) : hasProducts ? (
          <span
            className="nb-topbar__draft"
            style={{
              marginLeft: "auto",
              color: "#15803d",
              background: "#f0fdf4",
              borderColor: "#bbf7d0",
            }}
          >
            <Check size={11} aria-hidden="true" />
            Saved
          </span>
        ) : null}
      </div>

      {/* Header band */}
      <header className="nl-header">
        <div className="nl-header__row">
          <div className="nl-header__title-block">
            <span className="nl-header__eyebrow">
              <PackageCheck size={11} aria-hidden="true" />
              Review queue
            </span>
            <h1 className="nl-header__title">Pending products</h1>
            <p className="nl-header__sub">
              Review uploaded products before promoting them to the live catalog.
            </p>
          </div>

          {hasProducts && (
            <div className="nl-header__actions">
              {editedCount > 0 && (
                <button
                  type="button"
                  className="nb-btn nb-btn--ghost"
                  onClick={handleDiscard}
                  disabled={isSubmitting}
                  title="Discard all unsaved edits"
                >
                  Discard edits
                </button>
              )}
              <button
                type="button"
                className="nb-btn nb-btn--ghost"
                onClick={handleSaveDraft}
                disabled={isSubmitting || editedCount === 0}
                title={editedCount === 0 ? "No unsaved changes" : "Save edits without publishing"}
              >
                <CloudUpload size={14} aria-hidden="true" />
                Save to pending{editedCount > 0 ? ` (${editedCount})` : ""}
              </button>
              {canEditCost && (
                <button
                  type="button"
                  className="nb-btn nb-btn--primary"
                  onClick={handleSubmitClick}
                  disabled={isSubmitting || selectedCount === 0}
                  title={
                    selectedCount === 0
                      ? "Select at least one row to publish"
                      : `Publish ${selectedCount} row(s) to the live catalog`
                  }
                >
                  {isSubmitting ? (
                    <>
                      <span
                        style={{
                          display: "inline-block",
                          width: 14,
                          height: 14,
                          border: "2px solid currentColor",
                          borderTopColor: "transparent",
                          borderRadius: "50%",
                          animation: "nb-spin 0.7s linear infinite",
                        }}
                        role="status"
                      />
                      Submitting…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={14} aria-hidden="true" />
                      Publish{selectedCount > 0 ? ` (${selectedCount})` : ""}
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {showWorkspace && (
          <div className="nl-stats" role="group" aria-label="Workspace stats">
            <span className="nl-stat nl-stat--total">
              <strong>{totalRowsLabel}</strong>
              <em>{totalRowsHint || "total rows"}</em>
            </span>
            <span
              className={`nl-stat ${
                selectedCount > 0 ? "nl-stat--active" : "nl-stat--neutral"
              }`}
            >
              <strong>{selectedCount}</strong>
              <em>{selectedCount > 0 ? "selected" : "none selected"}</em>
            </span>
            <span
              className={`nl-stat ${
                editedCount > 0 ? "nl-stat--inactive" : "nl-stat--neutral"
              }`}
              style={
                editedCount > 0
                  ? {
                      background: "#fffbeb",
                      borderColor: "#fde68a",
                      color: "#b45309",
                    }
                  : undefined
              }
            >
              <strong>{editedCount}</strong>
              <em>{editedCount > 0 ? "unsaved" : "all synced"}</em>
            </span>
            <span
              className={`nl-stat ${
                errorCount > 0 ? "nl-stat--inactive" : "nl-stat--neutral"
              }`}
            >
              <strong>{errorCount}</strong>
              <em>{errorCount > 0 ? "with issues" : "looks clean"}</em>
            </span>
          </div>
        )}
      </header>

      {/* Body */}
      <main style={{ padding: "16px 24px 0", display: "flex", flexDirection: "column", gap: 20 }}>
        {isLoadingData && !hasProducts && (
          <div className="nl-empty">
            <span className="nl-empty__art">
              <span
                style={{
                  display: "inline-block",
                  width: 26,
                  height: 26,
                  border: "2px solid currentColor",
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  animation: "nb-spin 0.7s linear infinite",
                }}
                role="status"
                aria-label="Loading"
              />
            </span>
            <h3 className="nl-empty__title">Loading pending products…</h3>
            <p className="nl-empty__sub">Pulling staged rows from the server.</p>
          </div>
        )}

        {/* Truly empty: no filters in play, no products. Shows the upload CTA. */}
        {!isLoadingData && !hasProducts && !hasActiveFilters && (
          <div className="nl-empty">
            <span className="nl-empty__art">
              <Inbox size={26} aria-hidden="true" />
            </span>
            <h3 className="nl-empty__title">No pending products</h3>
            <p className="nl-empty__sub">
              Upload a CSV or Excel file to stage products for review before they appear in your live catalog.
            </p>
            <button
              type="button"
              className="nb-btn nb-btn--primary"
              onClick={() => navigate("/addproduct")}
            >
              <Upload size={14} aria-hidden="true" />
              Upload products
            </button>
          </div>
        )}

        {/* Workspace mounts whenever there's data OR an active filter — so the
         * filter bar stays visible after a zero-match search and the user can
         * always reset without navigating away. */}
        {showWorkspace && (
          <>
            <PendingProductsFilter
              filters={filters}
              onFilterSubmit={handleFilterSubmit}
              categoryOptions={proOptionsCategory}
              brandOptions={proOptionsBrand}
            />

            {showFilteredEmpty ? (
              <div className="nl-empty">
                <span className="nl-empty__art">
                  <SearchX size={26} aria-hidden="true" />
                </span>
                <h3 className="nl-empty__title">No products match your filters</h3>
                <p className="nl-empty__sub">
                  Try a different brand, category or search term — or clear the filters to see every pending product again.
                </p>
                <div style={{ display: "inline-flex", gap: 8 }}>
                  <button
                    type="button"
                    className="nb-btn nb-btn--ghost"
                    onClick={() => handleFilterSubmit({ ...DEFAULT_FILTERS })}
                  >
                    <FilterX size={14} aria-hidden="true" />
                    Clear filters
                  </button>
                  <button
                    type="button"
                    className="nb-btn nb-btn--ghost"
                    onClick={() => navigate("/addproduct")}
                  >
                    <Upload size={14} aria-hidden="true" />
                    Upload more
                  </button>
                </div>
              </div>
            ) : (
              <>
                <PendingProductsTable
                  products={products}
                  columns={columns}
                  visibleColumns={visibleColumns}
                  selectedRows={selectedRows}
                  selectedCount={selectedCount}
                  isLoadingData={isLoadingData}
                  validationErrors={validationErrors}
                  rowErrors={rowErrors}
                  requiredFields={requiredFields}
                  brandOptions={proOptionsBrand}
                  occasionOptions={proOptionsOccassions}
                  colorOptions={proOptionsColor}
                  sizeOptions={proOptionsSize}
                  categoryOptions={proOptionsCategory}
                  typeOptions={proOptionsType}
                  subCategoryOptions={proOptionsSubCategory}
                  onSelectRow={handleSelectRow}
                  onSelectAll={handleSelectAll}
                  onAddRow={handleAddRow}
                  onDuplicateRows={handleDuplicateRows}
                  onDeleteRows={handleDeleteRows}
                  onShowColumnModal={handleShowColumnModal}
                  onShowBulkEditModal={handleShowBulkEditModal}
                  onCellEdit={handleCellEdit}
                  onCellSave={handleCellSave}
                  onOpenCategoryModal={handleOpenCategoryModal}
                  editingCell={editingCell}
                  editValue={editValue}
                  setEditValue={setEditValue}
                  setEditingCell={setEditingCell}
                />

                <PendingProductsFooter
                  page={page}
                  perPage={perPage}
                  knownTotal={knownTotal}
                  knownTotalPages={knownTotalPages}
                  hasMore={hasMore}
                  productsOnPage={products.length}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                />
              </>
            )}
          </>
        )}

        {bulkSyncErrors.length > 0 && (
          <TaxonomyErrorReport
            errors={bulkSyncErrors}
            title={`Couldn't save ${bulkSyncErrors.length} row${bulkSyncErrors.length > 1 ? "s" : ""}`}
            subtitle="These edits weren't accepted by the server. The affected rows are highlighted in the table — fix the issues below and try again."
            onClose={() => setBulkSyncErrors([])}
          />
        )}

        {bulkInsertErrors.length > 0 && (
          <TaxonomyErrorReport
            errors={bulkInsertErrors}
            title={`${bulkInsertErrors.length} product(s) failed to save`}
            subtitle="The rows below were not promoted to the product list. Fix the taxonomy values (or other errors) and resubmit — failed rows stay in this pending list."
            onClose={() => setBulkInsertErrors([])}
          />
        )}
      </main>

      {showToast && (
        <div
          className="fixed top-4 right-4 z-[1080] pointer-events-none"
          role="status"
          aria-live="polite"
        >
          <div
            className={cn(
              "relative pointer-events-auto flex items-start gap-3 bg-card border rounded-xl shadow-lg pl-[18px] pr-[14px] py-[14px] w-[min(420px,calc(100vw-2rem))] text-sm text-foreground overflow-hidden",
              "before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1",
              "animate-in fade-in slide-in-from-top-2 duration-200",
              toastAccent.stripe
            )}
          >
            <div
              className={cn(
                "shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white mt-px",
                toastAccent.iconBg
              )}
              aria-hidden="true"
            >
              <ToastIcon className="h-4 w-4" strokeWidth={2.5} />
            </div>
            <div className="flex-1 leading-relaxed whitespace-pre-wrap break-words">
              {toastMessage}
            </div>
            <button
              type="button"
              onClick={() => setShowToast(false)}
              aria-label="Close"
              className="shrink-0 -mt-0.5 -mr-1 text-muted-foreground hover:bg-muted hover:text-foreground rounded-md p-1 transition-colors leading-none"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {showColumnModal && (
        <ColumnVisibilityModal
          columns={columns}
          visibleColumns={visibleColumns}
          onToggle={handleColumnToggle}
          onClose={() => setShowColumnModal(false)}
        />
      )}

      {showBulkEditModal && (
        <BulkEditModal
          bulkEditValues={bulkEditValues}
          setBulkEditValues={setBulkEditValues}
          onApply={handleApplyBulkEdit}
          onClose={() => {
            setShowBulkEditModal(false);
            setBulkEditValues({});
          }}
          typeOptions={proOptionsType}
          subCategoryOptions={proOptionsSubCategory}
        />
      )}

      {showCategorySubcategoryModal && (
        <CategorySubcategoryModal
          modalValue={modalValue}
          setModalValue={setModalValue}
          categoryOptions={proOptionsCategory}
          subCategories={subCategories}
          setSubCategories={setSubCategories}
          onDone={handleCategoryModalDone}
          onClose={() => setShowCategorySubcategoryModal(false)}
        />
      )}

      {showConfirmModal && (
        <ConfirmSubmitModal
          selectedCount={selectedCount}
          onConfirm={handleConfirmSubmit}
          onClose={() => setShowConfirmModal(false)}
        />
      )}

      {showDeleteConfirm && (
        <DeleteConfirmModal
          count={selectedCount}
          onConfirm={handleConfirmDelete}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
};

export default PendingProducts;
